import type { Todo, UpdateTodoBody } from '@kanto/shared';
import { useState, type ReactElement, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { errorText } from '../api/client';
import {
  useArchiveTodo,
  useCreateTodo,
  useRestoreTodo,
  useTodos,
  useUpdateTodo,
} from '../api/hooks';
import { AppShell } from '../components/AppShell';
import { OfflineNotice } from '../components/OfflineNotice';
import { Panel } from '../components/Panel';
import { TagFacet } from '../components/TagFacet';
import { TodoComposer } from '../components/TodoComposer';
import { TodoRow } from '../components/TodoRow';
import { isOverdue, isToday } from '../lib/dates';
import { useT, type MessageKey } from '../lib/i18n';

/**
 * The list, and the six states it can be in.
 *
 * Drawing only the populated one is how the other five ship broken: the empty
 * list, the first second, a server that refused, a phone with no connection, and
 * the afternoon everything is actually done are all states somebody meets, and
 * only one of them is the one being designed for.
 *
 * There is no archive screen. Archiving keeps the row, and the strip below offers
 * the way back; the screen that lists what has been archived arrives when
 * something has been archived long enough to be missed (D260814j).
 */

/**
 * Open, Today, All. Computed here rather than asked of the server: the whole list
 * is a few dozen rows, and a filter that costs a round trip is a filter that shows
 * a spinner for a question already answered on screen.
 *
 * **Today includes what is late.** A list called Today that hid yesterday's
 * unfinished todo would be the one thing a due date is for, quietly not working.
 */
const FILTERS = [
  { id: 'open', label: 'todos.filterOpen', keep: (todo: Todo) => todo.doneAt === null },
  {
    id: 'today',
    label: 'todos.filterToday',
    keep: (todo: Todo) =>
      todo.doneAt === null && todo.dueOn !== null && (isToday(todo.dueOn) || isOverdue(todo.dueOn)),
  },
  { id: 'all', label: 'todos.filterAll', keep: () => true },
] satisfies { id: string; label: MessageKey; keep: (todo: Todo) => boolean }[];

type FilterId = (typeof FILTERS)[number]['id'];

/**
 * Uneven on purpose. Four bars of one length read as a table being drawn; four of
 * different lengths read as sentences that have not arrived yet, which is what
 * they stand in for.
 */
const SKELETON_WIDTHS = ['55%', '72%', '40%', '64%'];

export default function TodosPage(): ReactElement {
  const t = useT();
  const { data, error, isPending, fetchStatus, refetch } = useTodos();
  const create = useCreateTodo();
  const update = useUpdateTodo();
  const archive = useArchiveTodo();
  const restore = useRestoreTodo();

  /**
   * The row a search hit named, if this screen was reached from the palette.
   *
   * In the address rather than in navigation state, so that the link is one a
   * person can keep: reloading it lands on the same row, which is not true of
   * anything held in the router's memory.
   */
  const [params] = useSearchParams();
  const highlight = params.get('highlight');

  const [filter, setFilter] = useState<FilterId>('open');
  /** The tag facet, cumulative with the filter above rather than replacing it. */
  const [tag, setTag] = useState<string | null>(null);
  /** Archived in this sitting, and therefore still offered back. */
  const [archived, setArchived] = useState<string[]>([]);

  const todos = data?.todos ?? [];
  const done = todos.filter((todo) => todo.doneAt !== null);
  const allDone = todos.length > 0 && done.length === todos.length;

  /**
   * The tags on screen, plus the one selected even after its last row has left
   * the list — archiving the only todo carrying a tag would otherwise remove the
   * chip that is the way out of the filter.
   */
  const facetTags = [
    ...new Set([...todos.flatMap((todo) => todo.tags), ...(tag === null ? [] : [tag])]),
  ].sort();

  // The facet first, so the counts on the three filters are counts of what
  // pressing one would actually show.
  const tagged = tag === null ? todos : todos.filter((todo) => todo.tags.includes(tag));
  const shown = tagged.filter(FILTERS.find((entry) => entry.id === filter)!.keep);
  const shownOpen = shown.filter((todo) => todo.doneAt === null);
  const shownDone = shown.filter((todo) => todo.doneAt !== null);

  const onArchive = (id: string): void => {
    archive.mutate(id);
    // Remembered rather than read back from the list: the row is gone from it by
    // the time this runs, which is the point of archiving optimistically.
    setArchived((ids) => [...ids, id]);
  };

  /**
   * Offline, rather than broken — the difference being that nothing is wrong.
   *
   * Two signals, because one of them does not cover the case that matters most.
   * TanStack Query **pauses** a fetch rather than failing it when the connection
   * drops while the application is open. But its online manager starts out
   * assuming a connection and only learns otherwise from an `offline` event, so
   * an application opened from a home screen in a tunnel never sees one: that
   * fetch fails like any other, and `navigator.onLine` is what says why.
   *
   * Only the negative direction of that flag is trusted, which is the direction
   * that is reliable: `false` means the browser has no network at all, while
   * `true` means only that it has an interface. A failure with `true` is
   * therefore left to the error state below, where the retry belongs.
   */
  if ((fetchStatus === 'paused' || !navigator.onLine) && !data) {
    return (
      <Screen>
        <OfflineNotice />
      </Screen>
    );
  }

  if (isPending) {
    return (
      <Screen>
        <TodoComposer onCreate={() => undefined} pending />
        {/* Four rows the height of real ones. A spinner in the middle of the page
            would be replaced by a list of a different height, and the whole
            screen would jump the moment it arrived. */}
        <ul
          className="mt-4 animate-pulse divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface"
          role="status"
          aria-label={t('common.loadingLabel')}
        >
          {SKELETON_WIDTHS.map((width, row) => (
            <li key={row} className="flex items-center gap-3 px-4 py-3">
              <span className="size-5 shrink-0 rounded bg-line" />
              <span className="h-4 rounded bg-line" style={{ width }} />
            </li>
          ))}
        </ul>
      </Screen>
    );
  }

  if (error) {
    return (
      <Screen>
        <Panel
          tone="alert"
          title={t('todos.errorTitle')}
          // The server's own words. It knows why it refused, and it has already
          // written that in the language this page asked for.
          body={errorText(error, t('todos.errorFallback'))}
          icon={
            <path d="M12 8v5M12 16h.01M10.3 3.9 2.4 17a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
          }
          action={
            <button
              type="button"
              onClick={() => void refetch()}
              className="min-h-11 rounded-lg bg-accent px-4 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-dim"
            >
              {t('todos.retry')}
            </button>
          }
        />
      </Screen>
    );
  }

  return (
    <Screen>
      <TodoComposer onCreate={(body) => create.mutate(body)} pending={create.isPending} />

      {archived.length > 0 && (
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-line bg-accent-soft px-4 py-2.5 text-sm">
          <span className="flex-1 text-ink-700">{t('todos.archived', archived.length)}</span>
          <button
            type="button"
            onClick={() => {
              for (const id of archived) restore.mutate(id);
              setArchived([]);
            }}
            className="min-h-9 rounded-lg px-2 font-semibold text-accent hover:underline"
          >
            {t('todos.undo')}
          </button>
          <button
            type="button"
            onClick={() => setArchived([])}
            aria-label={t('todos.dismiss')}
            className="flex min-h-9 min-w-9 items-center justify-center rounded-lg text-ink-400 hover:text-ink-900"
          >
            <svg
              viewBox="0 0 24 24"
              className="size-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
      )}

      {todos.length > 0 && (
        <div role="group" aria-label={t('todos.filters')} className="mt-4 flex items-center gap-1">
          {FILTERS.map((entry) => {
            const count = tagged.filter(entry.keep).length;
            return (
              <button
                key={entry.id}
                type="button"
                // `aria-pressed` and not a tab: nothing here is a tab panel, and
                // these three are a state the list is in rather than three places
                // to be.
                aria-pressed={entry.id === filter}
                onClick={() => setFilter(entry.id)}
                className={`flex min-h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-colors ${
                  entry.id === filter
                    ? 'bg-accent-soft text-accent'
                    : 'text-ink-500 hover:text-ink-900'
                }`}
              >
                {t(entry.label)}
                <span className="text-xs tabular-nums opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      <TagFacet tags={facetTags} selected={tag} onSelect={setTag} />

      {todos.length === 0 && (
        <Panel
          tone="quiet"
          dashed
          title={t('todos.emptyTitle')}
          body={t('todos.emptyBody')}
          icon={<path d="M4 7h16M4 12h16M4 17h9" />}
        />
      )}

      {allDone && filter !== 'all' && (
        <Panel
          tone="accent"
          title={t('todos.allDoneTitle')}
          body={t('todos.allDone', done.length)}
          icon={<path d="m5 13 4 4L19 7" />}
        />
      )}

      {/* Todos exist, and none of them is in this filter. Not one of the states
          above: the list is neither empty nor finished, and an area left blank
          would read as one that failed to draw. */}
      {todos.length > 0 && shown.length === 0 && !allDone && (
        <Panel
          tone="quiet"
          dashed
          title={t('todos.nothingHere')}
          body={t('todos.nothingHereBody')}
          icon={<path d="M4 7h16M4 12h16M4 17h9" />}
        />
      )}

      <TodoList
        todos={shownOpen}
        onUpdate={update.mutate}
        onArchive={onArchive}
        highlight={highlight}
      />

      {shownDone.length > 0 && (
        <>
          <h2 className="mt-8 mb-2 text-sm font-semibold tracking-wide text-ink-500 uppercase">
            {t('todos.done')}
          </h2>
          <TodoList
            todos={shownDone}
            onUpdate={update.mutate}
            onArchive={onArchive}
            highlight={highlight}
          />
        </>
      )}
    </Screen>
  );
}

/**
 * One card holding rows separated by a hairline, rather than a card per row: a
 * list of cards separates what is meant to be read as one thing.
 */
function TodoList({
  todos,
  onUpdate,
  onArchive,
  highlight,
}: {
  todos: Todo[];
  onUpdate: (variables: { id: string; patch: UpdateTodoBody }) => void;
  onArchive: (id: string) => void;
  highlight: string | null;
}): ReactElement | null {
  if (todos.length === 0) return null;

  return (
    <ul className="mt-4 divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface">
      {todos.map((todo) => (
        <TodoRow
          key={todo.id}
          todo={todo}
          onUpdate={(patch) => onUpdate({ id: todo.id, patch })}
          onArchive={() => onArchive(todo.id)}
          highlighted={todo.id === highlight}
        />
      ))}
    </ul>
  );
}

function Screen({ children }: { children: ReactNode }): ReactElement {
  const t = useT();
  return (
    <AppShell>
      <h1 className="mb-4 text-2xl font-semibold tracking-tight text-ink-900">
        {t('module.todos')}
      </h1>
      {children}
    </AppShell>
  );
}
